import { Dayjs } from 'dayjs'
import { formatAsDate, parseDate, createDate, now, startOfMonth, endOfMonth } from '../../config/dayjsConfig'
import { FiscalYear, Period } from '../../types/fiscalYear/FiscalYear'
import { PeriodGroup } from '../../types/periodGroup/PeriodGroup'

/**
 * Laskee kuukausien määrän alku- ja loppukuukauden välillä (0-indeksoitu).
 * Tukee vuodenvaihteen yli menevää jaksoa (esim. marraskuu -> maaliskuu).
 */
export const periodLength = (startMonth: number, endMonth: number): number => {
  return endMonth >= startMonth ? endMonth - startMonth + 1 : 12 - startMonth + endMonth + 1
}

// Antaa kuukausia päivämäärien väliltä
export const enumerateDaysBetweenDates = (
  startDate: string | Dayjs | null,
  endDate: string | Dayjs | null
): Dayjs[] => {
  const dates: Dayjs[] = []
  if (startDate === null || endDate === null) return dates

  const start = typeof startDate === 'string' ? parseDate(startDate)?.startOf('month') : startDate.startOf('month')
  const end = typeof endDate === 'string' ? parseDate(endDate)?.startOf('month') : endDate.startOf('month')

  if (!start || !end) return dates

  let loop = start.clone()
  do {
    dates.push(loop.clone())
    loop = loop.add(1, 'month')
  } while (loop.diff(end) <= 0)

  return dates
}

export const generatePeriodGroupPeriods = (periodGroup: PeriodGroup): Period[] => {
  if (!periodGroup?.startDate && !periodGroup?.endDate && !periodGroup) return []

  const periods = enumerateDaysBetweenDates(periodGroup.startDate, periodGroup.endDate)

  return periods.map((period, index) => {
    return {
      period: index + 1,
      year: period.year(),
      month: period.month() + 1
    }
  })
}

// Periodgroupin luonti alku ja loppupäivämäärällä
export const customPeriodGroup = (start: string | Dayjs, end: string | Dayjs): PeriodGroup => {
  const periodId = Date.now()
  const startDate = startOfMonth(start)
  const endDate = endOfMonth(end)
  const periodGroup = {
    id: periodId,
    startDate,
    endDate,
    periods: generatePeriodGroupPeriods({
      id: periodId,
      startDate,
      endDate
    } as PeriodGroup)
  }

  return periodGroup
}

// Neljännes periodGrouppien luonti vuosiluvulla
export const yearQuarters = (year: number): PeriodGroup[] => {
  const date = createDate(year || now().year(), 1, 1)
  if (!date) return []
  const quarters = [0, 1, 2, 3].map(i => {
    const startDate = date.add(i, 'Q').startOf('quarter')
    const enDate = date.add(i, 'Q').endOf('quarter')
    const id = Date.now() + i
    return {
      ...customPeriodGroup(startDate, enDate),
      id
    }
  })
  return quarters
}

// Splits fiscal years into period groups starting from given month with given length
// Uses the fiscal year's actual periods and clamps to fiscal year boundaries
// month: 0-indexed month (0 = January)
export const periodGroupsFromFiscalYears = (
  month: number,
  length: number,
  fiscalYears: FiscalYear[]
): PeriodGroup[] => {
  return fiscalYears.flatMap((f, i) => {
    const id = Date.now() + i + 1
    const month1 = month + 1

    let startIndex = -1
    let skipCount = 0
    for (let j = 0; j < length; j += 1) {
      const m = ((month1 - 1 + j) % 12) + 1
      startIndex = f.periods.findIndex(p => p.month === m)
      if (startIndex !== -1) {
        skipCount = j
        break
      }
    }
    const filteredPeriods = startIndex === -1 ? [] : f.periods.slice(startIndex, startIndex + length - skipCount)

    if (filteredPeriods.length === 0) {
      return []
    }

    const firstPeriod = filteredPeriods[0]
    const lastPeriod = filteredPeriods[filteredPeriods.length - 1]
    const startDate = createDate(firstPeriod.year, firstPeriod.month, 1)
    const endDate = createDate(lastPeriod.year, lastPeriod.month, 1)?.endOf('M')

    if (!startDate || !endDate) {
      return []
    }

    return {
      id,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      periods: filteredPeriods.map((p, idx) => ({ ...p, period: idx + 1 })),
      budgetable: f.budgetable
    }
  })
}

// Tekee periodGroupista vertailukauden
// Siirtää alku- ja loppupäivää vuodella taaksepäinj ja kääntää id:n miinusmerkkiseksi
export const comparativePeriodGroup = (periodGroup: PeriodGroup): PeriodGroup => {
  const parsedStart = parseDate(periodGroup.startDate)
  const parsedEnd = parseDate(periodGroup.endDate)
  return {
    id: -periodGroup.id,
    startDate: parsedStart?.subtract(1, 'year').format('YYYY-MM-DD') || '',
    endDate: parsedEnd?.subtract(1, 'year').format('YYYY-MM-DD') || '',
    periods: periodGroup.periods.map(({ year, ...rest }) => ({ ...rest, year: year - 1 }))
  }
}
